import React, {
  FC,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { WebUxTitle } from '../WebUxContext';
import { Table } from 'antd';
import {
  AccountTransaction,
  useBusinessContext,
} from '../../context/BusinessContext';
import { DayJs, Utils } from '../../common/Utils';
import { useConnection } from '../../connection/connection';
import classNames from 'classnames';

export type GeneralLedgerListType = {
  update: (
    account: string,
    site?: string,
    from?: DayJs.Dayjs,
    to?: DayJs.Dayjs,
  ) => void;
};

export type GeneralLedgerListProps = {
  showAccountHeader?: boolean;
};

export const GeneralLedgerList: ForwardRefRenderFunction<
  GeneralLedgerListType,
  GeneralLedgerListProps
> = (props, ref) => {
  const connection = useConnection();
  const business = useBusinessContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [currentAccount, setCurrentAccount] = useState<{
    id: string;
    name: string;
    category: string;
    type: string;
    site?: string;
    startBalance: string;
    endBalance: string;
    balanceDiff: string;
  }>();

  const accountCcy = useMemo(
    () =>
      (currentAccount && business.accounts[currentAccount.id]?.currency) ??
      business.currency,
    [business.accounts, business.currency, currentAccount],
  );

  const updateReport = useCallback(
    (account: string, site?: string, from?: DayJs.Dayjs, to?: DayJs.Dayjs) => {
      const _site = site ?? '0';
      const _from = from ?? DayJs();
      const _to = to ?? DayJs();

      setLoading(true);
      connection
        .get(`transactions/${business.businessId}/accounts/${account}`, {
          params: {
            site: _site,
            from: _from.format('YYYY-MM-DD'),
            to: _to.format('YYYY-MM-DD'),
          },
        })
        .then((res) => {
          const acc = business.accounts[account];
          const accType = business.accountTypes[acc.accountTypeId];
          const st = business.sites[_site];

          setCurrentAccount({
            id: account,
            name: business.accounts[account].name,
            type: accType.name,
            category: accType.accountCategory,
            site: st?.name ?? 'All Sites',
            startBalance: Utils.formatPrice(res.data.startBalance),
            endBalance: Utils.formatPrice(res.data.endBalance),
            balanceDiff: Utils.formatPrice(res.data.balanceDiff),
          });
          setTransactions([...res.data.transactions, res.data.totals]);
        })
        .finally(() => setLoading(false));
    },
    [
      business.accountTypes,
      business.accounts,
      business.businessId,
      business.sites,
      connection,
    ],
  );

  useImperativeHandle(ref, () => ({
    update: updateReport,
  }));

  const columns = useMemo(() => {
    return [
      {
        key: 'date',
        title: 'Date',
        width: 120,
        dataIndex: 'date',
        onCell: (record: any) => ({ colSpan: record.type === 'total' ? 3 : 1 }),
        onRow: (record: any) => ({ className: 'bg-red-500' }),
        render: (text: any, record: any) =>
          record.type === 'total' ? 'Totals and ending balance' : text,
      },
      {
        key: 'site',
        title: 'Site',
        dataIndex: 'rk',
        onCell: (record: any) => ({ colSpan: record.type === 'total' ? 0 : 1 }),
        render: (rk: string, record: any) =>
          record.type === 'total' ? '' : business.sites[rk.split(':')[1]].name,
      },
      {
        key: 'description',
        title: 'Description',
        dataIndex: 'description',
        onCell: (record: any) => ({ colSpan: record.type === 'total' ? 0 : 1 }),
      },
      {
        key: 'debit',
        title: 'Debit',
        dataIndex: 'amount',
        align: 'right',
        width: 160,
        render: (val: number, record: any) =>
          record.type === 'total' ? (
            <span className="font-bold">
              {`${accountCcy} ${Utils.formatPrice(record.debit)}`}
            </span>
          ) : val >= 0 ? (
            `${accountCcy} ${Utils.formatPrice(Utils.getAccountTxAmount(record))}`
          ) : (
            ''
          ),
      },
      {
        key: 'credit',
        title: 'Credit',
        dataIndex: 'amount',
        align: 'right',
        width: 160,
        render: (val: number, record: any) =>
          record.type === 'total' ? (
            <span className="font-bold">{`${accountCcy} ${Utils.formatPrice(-record.credit)}`}</span>
          ) : val < 0 ? (
            `${accountCcy} ${Utils.formatPrice(-Utils.getAccountTxAmount(record))}`
          ) : (
            ''
          ),
      },
      {
        key: 'balance',
        title: 'Balance',
        align: 'right',
        dataIndex: 'balance',
        width: 160,
        render: (val: number, record: any) =>
          record.type === 'total' ? (
            <span className="font-bold">{`${accountCcy} ${Utils.formatPrice(val)}`}</span>
          ) : (
            `${accountCcy} ${Utils.formatPrice(val)}`
          ),
      },
    ] as any;
  }, [business.sites, accountCcy]);

  return (
    <div className="flex flex-col gap-2">
      <Table
        loading={loading}
        dataSource={transactions}
        columns={columns}
        pagination={false}
        onRow={(data, index) => ({
          className: classNames({
            'bg-slate-100': (data as any).type === 'total',
          }),
        })}
        title={() => (
          <>
            {currentAccount && (
              <div
                className="flex flex-col -m-[16px]"
                style={{ borderRadius: 20 }}
              >
                {(props.showAccountHeader ?? true) && (
                  <div className="flex flex-col bg-slate-200 px-[16px] py-[8px]">
                    <h2 className="my-0">{currentAccount.name}</h2>
                    <div>{`Under: ${currentAccount.site} > ${currentAccount.category} > ${currentAccount.type}`}</div>
                  </div>
                )}
                <div className="flex justify-between bg-slate-100 px-[16px] py-[8px]">
                  <span className="font-bold">Starting Balance</span>
                  <span className="font-bold">
                    ${accountCcy} {currentAccount?.startBalance ?? '0.00'}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
        footer={() => (
          <div className="w-full">
            <div className="flex justify-between bg-slate-200 -m-[16px] px-[16px] py-[8px] ">
              <div className="flex flex-col justify-between">
                <span className="font-bold">Balance Change</span>
                <span className=" text-xs">
                  Difference between starting and ending balances
                </span>
              </div>
              <div className="font-bold">
                {accountCcy} {currentAccount?.balanceDiff ?? '0.00'}
              </div>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default React.forwardRef<GeneralLedgerListType, GeneralLedgerListProps>(
  GeneralLedgerList,
);
